import React, { useEffect, useState } from 'react'
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppEmptyLine, AppNote, AppTableSkeleton, AskIrisButton, secondaryButtonSx } from '@components'
import { besidePanel } from '@components/AskIris/besidePanel'
import { pushRight } from '@app/@signals'
import { userData } from '@signals/user'
import { isIrisUser } from 'Layout/PmsAgentWidget'
import { dsColorNew } from '@palette'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { inUnit, money, signedMoney, diffInUnit } from '../@services/amounts'
import { activityHref } from '../@services/viewState'
import { deltaSentence, pickupLabel, unitWord, windowText } from '../@services/windowLabel'
import { cellEvidence } from '../@services/askEvidence'
import { fetchPushes } from '../@services/useLastChanges'
import { fetchDecision } from '../@services/decisionRow'
import { activityActions } from 'Pages/DsRepricing/Activity/@services/useActivity'
import { Change, DeltaPill } from './Headline'
import { ChangeMoneyCell, ObservedCell, OpenInActivity, PairCell, PassCell, PointsCell, ResolutionChip } from './PushRows'


const tile = { border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', p: 1.25, minWidth: 0 }
const tileTitle = { fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }
const SHOW = 5

function Tile({ title, children, sub }) {
	return <Box sx={tile}>
		<Typography sx={tileTitle}>{title}</Typography>
		<Box sx={{ fontSize: 18, fontWeight: 700, color: dsColorNew.textTitle, mt: 0.25 }}>{children}</Box>
		{!!sub && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{sub}</Typography>}
	</Box>
}

/**
 * The detail of a cell (Curro 2026-10-29, UX v1.1 (a)): the two pairs (market, your price), Δ vs market, and every
 * pass that decided the cell in the window, newest first, with its resolution — the id links to Activity filtered by
 * pass and cell. Stays open beside the Iris panel for Iris users (`besidePanel`, as Season's detail). Esc closes.
 */
export default function CellDetailDialog({ open, cell, data, view, todayIso, onClose, onOpenAcriss, askable = false }) {
	const [pushes, setPushes] = useState({ loading: true, items: [], total: 0, error: null })
	const [all, setAll] = useState(false)
	// The last pass's Activity row, for Ask Iris's `derivation` (CU-869fn0gcp v1.4); read only for Iris askers
	const [decision, setDecision] = useState(null)
	const key = cell ? `${cell.sipp}|${cell.pickup_date}|${view.office}|${view.lor}|${view.window}|${view.days}|${view.simulated}` : null
	useEffect(() => {
		if (!open || !cell) return undefined
		const ctl = new AbortController()
		setPushes({ loading: true, items: [], total: 0, error: null })
		setAll(false)
		setDecision(null)
		// only Iris users read the Activity row: the button is hidden to the rest (QA 2026-10-30)
		const ask = askable && isIrisUser(userData.value) && cell.status !== 'no_decision'
		fetchPushes(view, { pickup: cell.pickup_date, sipp: cell.sipp }, ctl.signal)
			.then(b => {
				if (ctl.signal.aborted) return
				setPushes({ loading: false, items: b.items || [], total: b.total || 0, error: null })
				if (!ask || !b.items?.length) return
				// a response for a closed or changed dialog is dropped (the effect's abort)
				fetchDecision({ push: b.items[0], data, list: activityActions.listDecisions, signal: ctl.signal })
					.then(row => { if (!ctl.signal.aborted) setDecision(row) })
			})
			.catch(e => { if (!ctl.signal.aborted) setPushes({ loading: false, items: [], total: 0, error: e?.message || 'The pushes could not be read.' }) })
		return () => ctl.abort()
	}, [open, key, askable])  // eslint-disable-line react-hooks/exhaustive-deps
	if (!open || !cell) return null

	const lor = data?.sample?.lor || view.lor
	const unit = view.unit
	const tz = data?.window?.tz
	const canAsk = askable && cell.status !== 'no_decision'
	const withIris = canAsk && isIrisUser(userData.value)
	const getAsk = () => cellEvidence({ cell, pushes: pushes.items, data, view, decision })
	const shown = all ? pushes.items : pushes.items.slice(0, SHOW)
	const whole = activityHref({ office: view.office, sipp: cell.sipp, pickup_from: cell.pickup_date, lor: view.lor })
	const ownDiff = diffInUnit(cell.own_start, cell.own_end, unit, lor)

	return <AppDialog open={open} onClose={onClose} maxWidth="lg" aria-labelledby="last-changes-cell-title" {...besidePanel({}, { withIris, right: pushRight.value })}>
		<AppDialogHeader icon="table-cells" title={<span id="last-changes-cell-title"><b>{cell.sipp}</b> — {pickupLabel(cell.pickup_date, todayIso)}</span>}
			subtitle={`${data?.sample?.office_name || view.office} · ${lor} days · window ${windowText(data?.window)} · ${cell.n_passes} pass${cell.n_passes === 1 ? '' : 'es'} · ${unitWord(unit)} price, EUR`}
			actions={canAsk ? <AskIrisButton variant="label" tooltip="Ask Iris about this cell" getAsk={getAsk}/> : null} onClose={onClose}/>
		<AppDialogBody sx={{ ...dialogBodySx, maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
			{cell.sim && <AppNote severity="info" sx={{ mb: 1.5 }}>This cell ends in a decision taken in simulation (SIM): it counts like a written one, but nothing was written to the PMS for it.</AppNote>}
			{!cell.sim && view.simulated === 'exclude' && cell.sim_decisions > 0 && <AppNote severity="info" sx={{ mb: 1.5 }}>{cell.sim_decisions} simulated {cell.sim_decisions === 1 ? 'decision is' : 'decisions are'} hidden by the Simulated filter: listed below, counted nowhere.</AppNote>}
			{cell.thin && <AppNote severity="warning" sx={{ mb: 1.5 }}>Few passes behind this cell ({cell.n_passes}): read it with care.</AppNote>}
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25, mb: 2 }}>
				<Tile title="Market · tier" sub={cell.market_change == null ? (cell.n_comp_min != null && cell.n_comp_min < 2 ? 'fewer than 2 competitors with a price' : 'no tier') : `mean of the competitors · at least ${cell.n_comp_min} with a price at every pass`}>
					{cell.market_change == null ? '—' : <>
						<Change ratio={cell.market_change} size={18}/>
						<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{money(inUnit(cell.market_start, unit, lor))} → {money(inUnit(cell.market_end, unit, lor))}</Typography>
					</>}
				</Tile>
				<Tile title={cell.sim ? 'Your price · simulated' : 'Your price'} sub="before the first pass → the target after the last pass that counts">
					{cell.status === 'incomparable' ? '—' : <>
						<Change ratio={cell.own_change} size={18}/>
						<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{money(inUnit(cell.own_start, unit, lor))} → {money(inUnit(cell.own_end, unit, lor))}{ownDiff != null ? ` (${signedMoney(ownDiff)})` : ''}</Typography>
					</>}
				</Tile>
				<Tile title="Δ vs market" sub="engine change − market change">
					<DeltaPill deltaPt={cell.delta_pt} suffix=""/>
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>{deltaSentence(cell.delta_pt, cell.own_change, cell.market_change)}</Typography>
				</Tile>
			</Box>
			<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, mb: 1 }}>Pushes that decided it · newest first <Box component="span" sx={{ fontWeight: 400, color: dsColorNew.textMuted }}>· {unitWord(unit)} price, EUR</Box></Typography>
			{pushes.loading && <AppTableSkeleton rows={4} columns={7}/>}
			{pushes.error && <AppNote severity="error">{pushes.error}</AppNote>}
			{!pushes.loading && !pushes.error && !pushes.items.length && <AppEmptyLine>No pass decided this cell in the window.</AppEmptyLine>}
			{!!shown.length && <Table size="small" sx={{ '& td': { fontSize: 12.5, py: 0.75 } }}>
				<TableHead><TableRow sx={{ '& th': { ...headCellSx, fontSize: 11, whiteSpace: 'nowrap' } }}>
					<TableCell>Pass</TableCell><TableCell>Strategy · product</TableCell><TableCell>Market before → after</TableCell><TableCell>Your price before → after</TableCell><TableCell title="The shelf price the next pass of this cell read on the site">Observed next pass</TableCell>
					<TableCell>Engine</TableCell><TableCell>Δ</TableCell><TableCell>Resolution</TableCell><TableCell align="center">Activity</TableCell>
				</TableRow></TableHead>
				<TableBody>
					{shown.map(p => <TableRow key={p.event_id || `${p.pass}-${p.ts}`} hover>
						<TableCell><PassCell push={p} tz={tz}/></TableCell>
						<TableCell sx={{ fontSize: 12, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>{[p.strategy_name, p.product_name].filter(Boolean).join(' · ') || '—'}</TableCell>
						<TableCell><PairCell before={p.market_before} after={p.market_after} unit={unit} lor={lor}/></TableCell>
						<TableCell><PairCell before={p.own_before} after={p.own_after} unit={unit} lor={lor}/></TableCell>
						<TableCell><ObservedCell observed={p.own_observed_after} target={p.own_after} unit={unit} lor={lor} simulated={p.sim && p.counted}/></TableCell>
						<TableCell><ChangeMoneyCell ratio={p.own_change} before={p.own_before} after={p.own_after} unit={unit} lor={lor}/></TableCell>
						<TableCell><PointsCell pt={p.delta_pt}/></TableCell>
						<TableCell><ResolutionChip push={p}/></TableCell>
						<TableCell align="center"><OpenInActivity push={p} view={view}/></TableCell>
					</TableRow>)}
				</TableBody>
			</Table>}
			{pushes.items.length > SHOW && !all && <Button variant="text" size="small" onClick={() => setAll(true)} sx={{ textTransform: 'none', mt: 1 }}>
				… {pushes.items.length - SHOW} more pass{pushes.items.length - SHOW === 1 ? '' : 'es'} · Show all {pushes.items.length}
			</Button>}
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1.5 }}>
				The market pair is the tier as the engine saw it at each pass (the previous pass of this cell → this one). A row that does not count (already set, not sent, or simulated while Simulated is Exclude) is listed but does not move the figures above; a simulated row that counts carries SIM. "Observed next pass" is the price the next pass of this cell read on the site; the figures above use the target the engine set.
			</Typography>
		</AppDialogBody>
		<AppDialogFooter>
			<Button variant="outlined" onClick={() => onOpenAcriss(cell.sipp)} sx={secondaryButtonSx}>{cell.sipp}, all dates</Button>
			<Button variant="outlined" component="a" href={whole} target="_blank" rel="noopener" startIcon={<IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 13 }}/>} sx={secondaryButtonSx}>Open in Activity</Button>
			<Box sx={{ flex: 1 }}/>
			<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
		</AppDialogFooter>
	</AppDialog>
}
